import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { AccessStatus } from "@/components/AccessStatus";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Ticket, Repeat } from "lucide-react";
import { toast } from "sonner";

export default function Billing() {
  const { user, refresh } = useAuth();
  const [access, setAccess] = useState(user?.access);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/stats").then(({ data }) => setAccess(data.access)).catch(() => {});
  }, []);

  const doAction = async (kind) => {
    setBusy(kind);
    try {
      const lookup_key = kind === "single" ? "single_analysis" : "monthly_subscription";
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
      setBusy("");
    }
  };

  const cancel = async () => {
    setBusy("cancel");
    try {
      const { data } = await api.post("/billing/cancel");
      setAccess(data.access);
      await refresh();
      toast.success("Subscription cancelled");
    } catch (err) {
      toast.error("Could not cancel.");
    } finally {
      setBusy("");
    }
  };

  return (
    <DashboardLayout
      title="Access & Plans"
      subtitle="Choose how you'd like to run communication analyses."
    >
      <div className="space-y-8">
        <AccessStatus access={access} />

        <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3 text-sm text-slate-400" data-testid="stripe-checkout-notice">
          Secure checkout is powered by Stripe. You'll be redirected to complete payment. In test mode, use card <span className="font-mono text-slate-300">4242 4242 4242 4242</span>, any future expiry and any CVC.
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Single */}
          <div className="rounded-xl border border-slate-800 bg-[#0A1124] p-8 flex flex-col">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-blue-500/40 bg-blue-500/10">
              <Ticket className="h-5 w-5 text-blue-400" />
            </div>
            <h3 className="mt-5 font-display text-xl font-bold text-white">Single Analysis</h3>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="font-mono text-4xl font-bold text-white">$1</span>
              <span className="text-slate-500">one-time</span>
            </div>
            <ul className="mt-6 space-y-3 text-sm text-slate-300 flex-1">
              {["One complete communication analysis", "Full transcript included", "Communication confidence scoring"].map((f) => (
                <li key={f} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-cyan-400" /> {f}
                </li>
              ))}
            </ul>
            <Button
              onClick={() => doAction("single")}
              disabled={busy === "single"}
              data-testid="buy-single-btn"
              className="mt-8 w-full h-11 bg-slate-800 text-white font-semibold hover:bg-slate-700 border border-slate-700"
            >
              {busy === "single" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Start for $1"}
            </Button>
          </div>

          {/* Monthly */}
          <div className="relative rounded-xl border border-cyan-400/40 bg-[#0A1124] p-8 flex flex-col gx-glow">
            <span className="absolute -top-3 left-8 rounded-full bg-cyan-400 px-3 py-1 text-xs font-semibold text-black">
              Most Popular
            </span>
            <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-cyan-400/40 bg-cyan-400/10">
              <Repeat className="h-5 w-5 text-cyan-400" />
            </div>
            <h3 className="mt-5 font-display text-xl font-bold text-white">Monthly Subscription</h3>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="font-mono text-4xl font-bold text-white">$36.95</span>
              <span className="text-slate-500">/ month</span>
            </div>
            <ul className="mt-6 space-y-3 text-sm text-slate-300 flex-1">
              {["Multiple uploads every month", "Long-term communication trend analysis", "Cross-conversation pattern tracking", "Full transcripts & downloadable reports"].map((f) => (
                <li key={f} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-cyan-400" /> {f}
                </li>
              ))}
            </ul>
            {access?.subscription_active ? (
              <Button
                onClick={cancel}
                disabled={busy === "cancel"}
                data-testid="cancel-sub-btn"
                variant="outline"
                className="mt-8 w-full h-11 border-slate-700 text-white hover:bg-slate-800 hover:text-white"
              >
                {busy === "cancel" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cancel subscription"}
              </Button>
            ) : (
              <Button
                onClick={() => doAction("subscribe")}
                disabled={busy === "subscribe"}
                data-testid="subscribe-btn"
                className="mt-8 w-full h-11 bg-cyan-400 text-black font-semibold hover:bg-cyan-300"
              >
                {busy === "subscribe" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Subscribe monthly"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
